import { Box, Container, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { API } from '../../../global/config/api';
import { useServerCall } from '../../../global/helpers/hooks';
import { BaseHeader, XsButton, XsSearchSelect, XsTextField, XsTableView } from '../../../global/ui';
import { IRecordsOrderRow } from '../../../swagger/gd/record';
import { IRoundsRow } from '../../../swagger/gd/round';
import { IDeviceRow, IDeviceResponse } from '../../../swagger/gd/codelist';
import { ListResponse, CodeListRequest } from '../../../swagger/shared/list';
import LoginStore from '../login/LoginStore';
import WaitingStore from './WaitingStore';
import { TColumnExt } from '../../../global/types/types';
import GlobalStore from '../../../global/stores/GlobalStore';

// 7 stavov žiadostí z zp waitingStore.js
export const REQUEST_STATUS_OPTIONS = [
  { _id: 'sent', name_ext: 'Prijatá' },
  { _id: 'paid', name_ext: 'Uhradená' },
  { _id: 'withdrawn', name_ext: 'Späťvzatá' },
  { _id: 'reject', name_ext: 'Zamietnutá' },
  { _id: 'contracted', name_ext: 'Zazmluvnená' },
  { _id: 'instack', name_ext: 'V zásobníku' },
  { _id: 'stopped', name_ext: 'Zastavené konanie' }
] as const;

type TRequestStatus = (typeof REQUEST_STATUS_OPTIONS)[number];

interface IWaitingFilterForm {
  request_number: string;
  round_id: (IRoundsRow & { _label?: string }) | null;
  device: IDeviceRow | null;
  request_state: TRequestStatus | null;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const columns: TColumnExt<IRecordsOrderRow>[] = [
  { Header: 'waiting.col_sequence', accessor: 'sequence', width: 50 },
  { Header: 'waiting.col_request_number', accessor: 'request_number', width: 80 },
  {
    Header: 'waiting.col_sent_date_time',
    accessor: 'sent_date_time',
    width: 100,
    Cell: ({ value }: any) => <span>{formatDate(value)}</span>
  },
  { Header: 'waiting.col_device_name', accessor: 'device_name', width: 120 },
  { Header: 'waiting.col_status_name_ext', accessor: 'status_name_ext', width: 90 },
  { Header: 'waiting.col_expected_amount', accessor: 'expected_voucher_amount', width: 100 },
  { Header: 'waiting.col_paid_voucher', accessor: 'paid_voucher', width: 100 }
];

function WaitingList() {
  const { t } = useTranslation();

  const form = useForm<IWaitingFilterForm>({
    defaultValues: {
      request_number: '',
      round_id: null,
      device: null,
      request_state: null
    }
  });
  const { control, handleSubmit, reset, watch } = form;

  const roundValue = watch('round_id');

  // --- Načítanie kôl (rounds) na mount cez recaptcha ---
  const roundsCall = useServerCall<object, ListResponse<IRoundsRow>>({
    key: 'waiting_rounds',
    mode: 'control',
    request: { method: 'GET', data: {} },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (response) => {
      const rows = response.data?.rows ?? [];
      WaitingStore.setRecordRounds(rows);
    }
  });

  // --- Načítanie zariadení pre filter (public, bez recaptcha) ---
  const devicesCall = useServerCall<CodeListRequest, IDeviceResponse>({
    key: 'waiting_devices',
    mode: 'control',
    request: { method: 'POST', url: API.devicePublic(), data: { light: true } },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (response) => {
      const data = response.data as unknown;
      const rows = Array.isArray(data) ? (data as IDeviceRow[]) : (response.data?.rows ?? []);
      WaitingStore.setFilterDevices(rows);
    }
  });

  // --- Hľadanie poradovníka ---
  const waitingCall = useServerCall<object, ListResponse<IRecordsOrderRow>>({
    key: 'waiting_list',
    mode: 'control',
    request: { method: 'GET' },
    queryOptions: { refetchOnWindowFocus: false, cacheTime: 0 },
    onSuccess: (response) => {
      WaitingStore.setData(response.data?.rows ?? []);
      GlobalStore.loading(false);
    },
    onError: () => {
      GlobalStore.loading(false);
    }
  });

  useEffect(() => {
    // Načítať zariadenia hneď (bez recaptcha)
    devicesCall.run();

    // Načítať kolá cez recaptcha
    (async () => {
      GlobalStore.loading(true);
      try {
        const token = await LoginStore.getRecaptchaToken('records_rounds');
        roundsCall.run({ url: API.recordRounds(token), data: {} });
      } finally {
        GlobalStore.loading(false);
      }
    })();
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    GlobalStore.loading(true);
    try {
      const token = await LoginStore.getRecaptchaToken('records_order');
      const filters: Array<{ associated_column: string; predicate: string; values: { id_value: string }[] }> = [];

      if (data.request_number) {
        filters.push({
          associated_column: 'request_number',
          predicate: '=',
          values: [{ id_value: data.request_number }]
        });
      }
      if (data.request_state?._id) {
        filters.push({
          associated_column: 'status_id',
          predicate: '=',
          values: [{ id_value: data.request_state._id }]
        });
      }
      if (data.device?._id) {
        filters.push({ associated_column: 'device_id', predicate: '=', values: [{ id_value: data.device._id }] });
      }
      if (data.round_id?.round_id) {
        filters.push({ associated_column: 'round_id', predicate: '=', values: [{ id_value: data.round_id.round_id }] });
      }

      waitingCall.run({ method: 'POST', url: API.waitingList(token), data: { filters } });
    } catch {
      GlobalStore.loading(false);
    }
  });

  const onReset = () => {
    reset({
      request_number: '',
      round_id: form.getValues('round_id'),
      device: null,
      request_state: null
    });
    onSubmit();
  };

  // Rounds transformované pre XsSearchSelect label
  const roundOptions = WaitingStore.recordRounds.map((r) => {
    const startDate = r.start ? r.start.slice(0, 10).split('-').reverse().join('.') : '';
    const endDate = r.validity_to ? r.validity_to.slice(0, 10).split('-').reverse().join('.') : '';
    const _label = [r.region_type_code_ext, r.devide_group_name_ext, startDate, endDate && `- ${endDate}`]
      .filter(Boolean)
      .join(' ');
    return { ...r, _label };
  });

  const tableData = {
    rows: WaitingStore.data,
    totalRows: WaitingStore.data.length
  };

  return (
    <>
      <BaseHeader />
      <Container maxWidth='lg'>
        <Grid container justifyContent='space-between' alignItems='center' style={{ marginTop: 32 }}>
          <Grid item>
            <Typography variant='h4'>{t('waiting.title')}</Typography>
          </Grid>
        </Grid>
        <Box pt={2}>
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              {/* Výber kola — odporúčaný pred načítaním */}
              <Grid container alignItems='center' spacing={2} style={{ marginBottom: 16 }}>
                <Grid item>
                  <Typography variant='body2' style={{ fontWeight: 'bold', color: '#c10b25' }}>
                    {t('waiting.round_required_hint')}
                  </Typography>
                </Grid>
                <Grid item style={{ minWidth: 300, flex: 1 }}>
                  <Controller
                    name='round_id'
                    control={control}
                    render={({ field }) => (
                      <XsSearchSelect<(typeof roundOptions)[0]>
                        {...field}
                        label={t('waiting.round_label')}
                        isClearable
                        options={roundOptions}
                        getOptionLabel={(o) => o._label ?? o.round_id}
                        getOptionValue={(o) => o.round_id}
                        isLoading={roundsCall.isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <XsButton type='submit' variant='contained' disabled={!roundValue} label='waiting.load_button' />
                </Grid>
              </Grid>

              {/* Separator + additonal filters */}
              <Grid container alignItems='center' style={{ gap: 8 }}>
                <Grid item>
                  <Typography variant='caption' style={{ fontWeight: 'bold' }}>
                    {t('waiting.filter_hint')}
                  </Typography>
                </Grid>
                <Grid item style={{ flex: 1 }}>
                  <div style={{ height: 1, backgroundColor: '#CED7D9', width: '100%' }} />
                </Grid>
              </Grid>

              <Grid container alignItems='center' spacing={2} style={{ marginTop: 8, marginBottom: 8 }}>
                <Grid item xs={3}>
                  <Controller
                    name='request_number'
                    control={control}
                    render={({ field }) => <XsTextField {...field} label={t('waiting.request_number_label')} />}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name='device'
                    control={control}
                    render={({ field }) => (
                      <XsSearchSelect<IDeviceRow>
                        {...field}
                        label={t('waiting.device_label')}
                        isClearable
                        options={WaitingStore.filterDevices}
                        getOptionLabel={(o) => o.name_ext ?? o._id}
                        getOptionValue={(o) => o._id}
                        isLoading={devicesCall.isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name='request_state'
                    control={control}
                    render={({ field }) => (
                      <XsSearchSelect<TRequestStatus>
                        {...field}
                        label={t('waiting.request_state_label')}
                        isClearable
                        options={REQUEST_STATUS_OPTIONS as unknown as TRequestStatus[]}
                        getOptionLabel={(o) => o.name_ext}
                        getOptionValue={(o) => o._id}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <XsButton
                    fullWidth={false}
                    variant='outlined'
                    color='primary'
                    icon={<i className='far fa-times' />}
                    disabled={!roundValue}
                    onClick={onReset}
                  />
                </Grid>
                <Grid item>
                  <XsButton
                    fullWidth={false}
                    variant='contained'
                    color='primary'
                    icon={<i className='far fa-search' />}
                    disabled={!roundValue}
                    onClick={onSubmit}
                  />
                </Grid>
              </Grid>
            </form>
          </FormProvider>

          {/* Tabuľka výsledkov — priamo z WaitingStore.data */}
          <Grid container>
            <Grid item xs={12}>
              <Box pt={1}>
                <XsTableView<IRecordsOrderRow>
                  name='waiting_table'
                  control='client'
                  columns={columns}
                  data={tableData}
                  pagination
                  initState={{ pageSize: 20 }}
                  loading={waitingCall.isPending}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
}

export default observer(WaitingList);
